import React, { useRef } from "react";
import { useTranslation } from "react-i18next";
import { ReactSVG } from "react-svg";
import { getIcon, getImage } from "@/constants";
import { SliderCustom } from "@/helpers/general";
import { useAppSelector } from "@/redux/hooks";
import { RootState } from "@/redux/store";
import { map } from "lodash";
import { ChevronLeft, ChevronRight } from "lucide-react";
import Slider from "react-slick";

const SuccessPartners: React.FC = () => {
    const { t } = useTranslation();
    const { isRTL } = useAppSelector((state: RootState) => state.locale);
    const refSlider = useRef<Slider | null>(null);

    const partners = [
        {
            name: "CSC",
            image: "csc.png",
            alt: "CSC Partner",
            maxHeight: "20",
            link: "https://portal.csc.gov.kw/webcenter/portal/CSCPortal",
        },
        {
            name: "PAAET",
            image: "PAAET-LOGO.png",
            alt: "PAAET Partner",
            link: "https://www.paaet.edu.kw/",
        },
        {
            name: "IELTS",
            image: "IELTS_1.png",
            alt: "IELTS Partner",
            maxHeight: "32",
            link: "https://book.ielts.idp.com/account/signin",
        },
        {
            name: "TOEFL",
            image: "toefl.png",
            alt: "TOEFL iBT Partner",
            maxHeight: "16",
            link: "https://www.ets.org/toefl.html?utm_source=google&utm_medium=cpc&utm_campaign=22568976180&utm_id=22568976180&utm_content=184558762287&utm_term=toefl&creativeId=757576940588&adgroupid=184558762287&targetid=kwd-11926941&utm_term=toefl&hsa_acc=%7Baccountid%7D&hsa_cam=22568976180&hsa_grp=184558762287&hsa_ad=757576940588&hsa_src=g&hsa_tgt=kwd-11926941&hsa_kw=toefl&hsa_mt=b&hsa_net=adwords&hsa_ver=3&gad_source=1&gad_campaignid=22568976180&gbraid=0AAAAADlVbMpPBlhuRojxw4894sRRu6OQ_",
        },
        {
            name: "ICDL",
            image: "icdl.png",
            alt: "ICDL Partner",
            maxHeight: "20",
            link: "https://icdlarabia.org/",
        },
        {
            name: "NCG",
            image: "ncg.png",
            alt: "NCG Partner",
            maxHeight: "20",
            link: "https://newcollegegroup.com",
        },
        {
            name: "OTAGO",
            image: "otago.png",
            alt: "OTAGO Partner",
            maxHeight: "20",
            link: "https://www.otago.ac.nz",
        },
    ];

    const settings = {
        rtl: isRTL,
        dots: false,
        infinite: true,
        autoplay: true,
        autoplaySpeed: 2500,
        pauseOnHover: true,
        speed: 600,
        swipeToSlide: true,
        arrows: false,
        slidesToShow: 6,
        slidesToScroll: 1,
        responsive: [
            { breakpoint: 1280, settings: { slidesToShow: 5 } },
            { breakpoint: 1024, settings: { slidesToShow: 4 } },
            { breakpoint: 768, settings: { slidesToShow: 2 } },
            { breakpoint: 640, settings: { slidesToShow: 2 } },
        ],
    };

    return (
        <div className="bg-white py-12 sm:py-8 rounded-3xl">
            <div className="hidden max-h-20 max-h-16 max-h-32 md:max-h-16 md:max-h-20 md:max-h-32"></div>
            <div className="container mx-auto px-6">
                {/* Header Section */}
                <div className="flex flex-1 flex-col justify-start items-center gap-y-2">
                    <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl capitalize">
                        {t("approved_partners")}
                    </h2>
                    <ReactSVG
                        src={getIcon("title_arrow.svg")}
                        className="relative w-20 text-orange-700 ltr:rotate-180"
                    />
                </div>

                {/* Partners Logos Slider */}
                <div className="relative mt-6 px-2 md:px-8">
                    <button
                        type="button"
                        aria-label="Previous"
                        onClick={() => refSlider?.current?.slickPrev()}
                        className="hidden lg:flex absolute top-1/2 -translate-y-1/2 ltr:left-0 rtl:right-0 z-50 items-center justify-center h-8 w-8 cursor-pointer text-gray-700 border border-gray-200 bg-gray-100 hover:bg-gray-200 rounded-xl"
                    >
                        <ChevronLeft className="h-5 w-5 rtl:rotate-180" />
                    </button>
                    <button
                        type="button"
                        aria-label="Next"
                        onClick={() => refSlider?.current?.slickNext()}
                        className="hidden lg:flex absolute top-1/2 -translate-y-1/2 ltr:right-0 rtl:left-0 z-50 items-center justify-center h-8 w-8 cursor-pointer text-gray-700 border border-gray-200 bg-gray-100 hover:bg-gray-200 rounded-xl"
                    >
                        <ChevronRight className="h-5 w-5 rtl:rotate-180" />
                    </button>
                    <SliderCustom {...settings} ref={refSlider}>
                        {map(partners, (p, i: number) => (
                            <div key={i} className="px-2 py-4">
                                <a
                                    href={p.link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label={p.alt}
                                    className="flex justify-center items-center p-2 bg-white border border-gray-300 rounded-2xl transition-all duration-300 hover:bg-gray-50 hover:shadow-md hover:scale-105 h-28 md:h-32"
                                >
                                    <img
                                        src={getImage(p.image)}
                                        alt={p.alt}
                                        className={`max-h-${p.maxHeight ?? `16`} md:max-h-${p.maxHeight ?? `20`} max-w-full w-auto object-contain`}
                                        loading="lazy"
                                    />
                                </a>
                            </div>
                        ))}
                    </SliderCustom>
                </div>
            </div>
        </div>
    );
};

export default SuccessPartners;
